import type { NavigateFunction } from 'react-router-dom';
import { PLUMAA_SCHEDULING_RETURN_URL_KEY } from '@/constants/schedulingHandoff';
import { encodeUrl } from './encodeUrl';
import {
	clearPublicSchedulingDraftFromLocalStorage,
	schedulingSelectedDateStillBookable,
} from './schedulingDraftAfterRegister';

/** Usuário clicou em Pagar na revisão sem estar logado; pagamento só após auth. */
export const SCHEDULING_PAY_AFTER_AUTH_KEY = 'scheduling_pay_after_auth';

export function saveSchedulingPayDraft(body: Sessions.Create): void {
	try {
		localStorage.setItem('session_data', JSON.stringify(body));
		localStorage.setItem(SCHEDULING_PAY_AFTER_AUTH_KEY, 'true');
		localStorage.setItem('isSchedulingFlow', 'true');
	} catch {
		// ignore
	}
}

/** Ao clicar em Pagar sem login: login em /login pode retomar sem apagar o rascunho. */
export function markSchedulingPayHandoffToAuth(): void {
	try {
		sessionStorage.setItem(
			PLUMAA_SCHEDULING_RETURN_URL_KEY,
			`/agendamento?d=${encodeUrl({ s: 3 })}`,
		);
	} catch {
		// ignore
	}
}

export function isSchedulingPayAfterAuthPending(): boolean {
	try {
		return (
			localStorage.getItem(SCHEDULING_PAY_AFTER_AUTH_KEY) === 'true' &&
			Boolean(localStorage.getItem('session_data'))
		);
	} catch {
		return false;
	}
}

export function readSchedulingPayDraft(): Sessions.Create | null {
	try {
		const raw = localStorage.getItem('session_data');
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Record<string, unknown>;
		const { user_id: _ignored, ...body } = parsed;
		return body as unknown as Sessions.Create;
	} catch {
		return null;
	}
}

export function clearSchedulingPayDraftSilently(): void {
	try {
		clearPublicSchedulingDraftFromLocalStorage();
		localStorage.removeItem(SCHEDULING_PAY_AFTER_AUTH_KEY);
		localStorage.removeItem('pAuth');
		localStorage.removeItem('rAuth');
		localStorage.removeItem('is_public_register');
		sessionStorage.removeItem(PLUMAA_SCHEDULING_RETURN_URL_KEY);
	} catch {
		// ignore
	}
}

export function navigateToAgendamentoStripeCheckoutStep(
	navigate: NavigateFunction,
): void {
	navigate(`/agendamento?d=${encodeUrl({ s: 4 })}&s_s=true`, {
		replace: true,
	});
}

export type ExecuteSchedulingPayAfterAuthParams = {
	createSession: (body: Sessions.Create) => Promise<Sessions.Session>;
	paySession: (params: {
		id: string;
		gateway: Sessions.PaymentGateway;
	}) => Promise<Sessions.PaySessionResponse>;
};

/** Cria sessão e redireciona ao Stripe. Limpa o rascunho antes do redirect. */
export async function executeSchedulingPayAfterAuth({
	createSession,
	paySession,
}: ExecuteSchedulingPayAfterAuthParams): Promise<
	| { ok: true; redirected: true }
	| { ok: true; redirected: false; session: Sessions.Session }
	| {
			ok: false;
			reason: 'not_pending' | 'invalid_draft' | 'expired' | 'error';
	  }
> {
	if (!isSchedulingPayAfterAuthPending()) {
		return { ok: false, reason: 'not_pending' };
	}

	const body = readSchedulingPayDraft();
	if (!body?.selected_date) {
		clearSchedulingPayDraftSilently();
		return { ok: false, reason: 'invalid_draft' };
	}

	if (!schedulingSelectedDateStillBookable(body.selected_date)) {
		clearSchedulingPayDraftSilently();
		return { ok: false, reason: 'expired' };
	}

	try {
		const session = await createSession(body);
		clearSchedulingPayDraftSilently();
		const response = await paySession({
			id: session.id,
			gateway: 'STRIPE',
		});
		if (response.url) {
			window.location.href = response.url;
			return { ok: true, redirected: true };
		}
		return { ok: true, redirected: false, session };
	} catch {
		clearSchedulingPayDraftSilently();
		return { ok: false, reason: 'error' };
	}
}
